import {
  buildCanvasNodeKind,
  ObjectActionPaneHeaderActions,
  useCanvasEntityObjectActions,
} from "@/object-actions";
import { useCanvasEntityNavigationHandlers } from "@/pages/canvas/hooks/useCanvasEntityNavigationHandlers";
import type { Entity } from "@cohesive/ui-components";
import { useMemo } from "react";

const NOOP = () => {};

/**
 * Registry-driven pane-header actions (Open in new tab, Copy URL) for an
 * integration entity detail pane. Replaces the per-pane hard-coded
 * "Open in <platform>" / "Copy link" buttons so labels and icons stay
 * consistent with the canvas entity toolbar and context menu.
 *
 * Renders nothing when the entity resolves no eligible actions (e.g. no web
 * link and no copyable URL). Must be rendered inside a `TooltipProvider`.
 */
export function EntityPaneHeaderObjectActions({
  entity,
  name,
}: {
  entity: Entity;
  name?: string;
}) {
  const kind = useMemo(
    () =>
      buildCanvasNodeKind("entity", "base" in entity ? entity.base : undefined),
    [entity],
  );

  const { handlers, hasWebLink } = useCanvasEntityNavigationHandlers(
    entity,
    // Standalone detail pane: never expose destructive/canvas-only actions.
    false,
    NOOP,
  );

  const { actions } = useCanvasEntityObjectActions({
    kind,
    surface: "pane-header",
    nodeHandlers: handlers,
    state: { canEdit: false, hasWebLink, hasDetailView: false },
  });

  if (!kind) return null;

  return (
    <ObjectActionPaneHeaderActions actions={actions} kind={kind} name={name} />
  );
}
